'use client';

import { Box, Stack, Typography } from '@mui/material';
import { DonutBlock, SectionTable } from './TickerPortfolioTabVisuals';
import type { PortfolioTabViewModel } from './TickerPortfolioTabClient';

type Props = {
  data: PortfolioTabViewModel;
  chartColors: string[];
  groupCount: (labels: string[]) => Array<{ label: string; value: number }>;
  groupSum: (items: Array<{ label: string; value: number | null }>) => Array<{ label: string; value: number }>;
  formatDateOrDash: (value: string | null) => string;
  formatOptionalNumber: (value: number | null) => string;
  formatOptionalCurrency: (value: number | null) => string;
};

export function PortfolioFinancialAssetsSection({
  data,
  chartColors,
  groupCount,
  groupSum,
  formatDateOrDash,
  formatOptionalNumber,
  formatOptionalCurrency,
}: Props) {
  if (data.financialAssets.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sem ativos financeiros detalhados reportados na CVM para o período mais recente.
      </Typography>
    );
  }
  const typeCount = groupCount(data.financialAssets.map((item) => item.type?.trim() || 'Sem tipo'));
  const typeValue = groupSum(
    data.financialAssets.map((item) => ({
      label: item.type?.trim() || 'Sem tipo',
      value: item.value,
    })),
  );
  const maturityGroups = groupCount(
    data.financialAssets.map((item) => {
      if (!item.maturityDate) return 'Sem vencimento informado';
      const raw = item.maturityDate.trim();
      const normalized = /^\d{2}\/\d{2}\/\d{4}$/.test(raw)
        ? `${raw.slice(6, 10)}-${raw.slice(3, 5)}-${raw.slice(0, 2)}`
        : raw;
      const date = new Date(`${normalized}T00:00:00Z`);
      if (Number.isNaN(date.getTime())) return 'Vencimento inválido';
      const now = new Date();
      const diffDays = (date.getTime() - now.getTime()) / (24 * 60 * 60 * 1000);
      if (diffDays < 0) return 'Vencido';
      if (diffDays <= 365) return 'Até 12 meses';
      return 'Acima de 12 meses';
    }),
  );
  const maturityCvmItems = data.maturityBreakdown
    .filter((item) => item.value !== null && item.value > 0)
    .map((item) => ({ label: item.label, value: item.value as number }));
  const indexerCvmItems = data.indexerBreakdown
    .filter((item) => item.value !== null && item.value > 0)
    .map((item) => ({ label: item.label, value: item.value as number }));

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(auto-fit, minmax(380px, 1fr))',
          },
          gap: 1.2,
        }}
      >
        <DonutBlock
          title='Ativos por tipo (qtd.)'
          items={typeCount}
          colors={chartColors}
          infoText='Quantidade de posições por tipo de ativo financeiro.'
        />
        <DonutBlock
          title='Ativos por tipo (valor)'
          items={typeValue}
          colors={chartColors}
          valueKind='currency'
          infoText='Valor total por tipo de ativo financeiro.'
        />
        <DonutBlock
          title='Ativos por vencimento'
          items={maturityGroups}
          colors={chartColors}
          infoText='Agrupa ativos por faixa de vencimento com base na data informada.'
        />
      </Box>
      {maturityCvmItems.length > 0 || indexerCvmItems.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.2,
          }}
        >
          {maturityCvmItems.length > 0 ? (
            <DonutBlock
              title='Vencimento da carteira (% valor total)'
              items={maturityCvmItems}
              colors={chartColors}
              valueKind='percentPoints'
              infoText='Percentual do valor total da carteira por faixa de vencimento (base CVM).'
            />
          ) : null}
          {indexerCvmItems.length > 0 ? (
            <DonutBlock
              title='Indexadores (% valor total)'
              items={indexerCvmItems}
              colors={chartColors}
              valueKind='percentPoints'
              infoText='Percentual do valor total por indexador (base CVM).'
            />
          ) : null}
        </Box>
      ) : null}
      <SectionTable
        headers={['Tipo', 'Emissor', 'CNPJ', 'Vencimento', 'Quantidade', 'Valor']}
        rows={data.financialAssets.map((item) => [
          item.type ?? '-',
          item.issuer ?? '-',
          item.issuerCnpj ?? '-',
          formatDateOrDash(item.maturityDate),
          formatOptionalNumber(item.quantity),
          formatOptionalCurrency(item.value),
        ])}
      />
    </Stack>
  );
}
